/**
 * Evidence rules.
 *
 * Canonical completeness logic lives in `@/lib/assessmentCompleteness`.
 * This module keeps the low-level helpers: threshold check, link
 * normalization/classification, and the legacy validators that delegate to the
 * canonical model.
 */

/**
 * Check if evidence is required based on score and threshold.
 * Uses item-level threshold when available, falls back to global default (50).
 */
export function isEvidenceRequired(
  scorePercent?: number | null,
  threshold: number = 50
) {
  if (scorePercent == null) return false;
  return scorePercent >= threshold;
}

/**
 * Items that were produced by the secure upload pipeline
 * (`upload-evidence` Edge Function). They are ALREADY persisted in
 * `assessment_evidence`, so the wizard must never re-persist them.
 *
 * Everything else the respondent types into the evidence box is an external
 * LINK and must be persisted as `evidence_type = 'link'`.
 *
 * NOTE: never classify by loose substrings such as "/evidence/" — real
 * SharePoint/Drive links commonly contain that path segment and were being
 * silently dropped instead of saved.
 */
export function isManagedEvidenceReference(item: string): boolean {
  const value = (item ?? "").trim();
  if (!value) return false;
  // Evidence UUID returned by the upload function.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) return true;
  // Supabase Storage object URL.
  if (value.includes("/storage/v1/object/")) return true;
  return false;
}

/**
 * Normalize a respondent-entered evidence link.
 * Adds a scheme when missing so the value round-trips through the DB and
 * opens correctly later. Returns null when the value cannot be a link.
 */
export function normalizeEvidenceLink(raw: string): string | null {
  const value = (raw ?? "").trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  // Reject obvious non-URLs (spaces, no dot, no path).
  if (/\s/.test(value)) return null;
  if (!/[.]/.test(value) && !value.startsWith("/")) return null;
  return `https://${value.replace(/^\/+/, "")}`;
}

/** True when the value is an acceptable evidence link. */
export function isValidEvidenceLink(raw: string): boolean {
  return normalizeEvidenceLink(raw) !== null;
}


/**
 * Validate all questions that require evidence before submission.
 * Returns array of question_ids that are missing required evidence.
 * Used for non-telecom_v1 questionnaires (requires_evidence flag based).
 */
export function validateEvidenceForSubmission(
  rows: Array<{
    question_id: number;
    requires_evidence?: boolean;
    evidence_threshold?: number | null;
  }>,
  responsesById: Map<
    number,
    { score: number | null; comment: string | null; evidence_links?: string[] }
  >
): number[] {
  const missing: number[] = [];

  for (const row of rows) {
    if (!row.requires_evidence) continue;

    const resp = responsesById.get(row.question_id);
    if (!resp || resp.score == null) continue;

    const threshold = row.evidence_threshold ?? 50;
    if (resp.score < threshold) continue;

    // Score meets threshold — check for evidence
    const links = resp.evidence_links ?? [];
    if (links.length === 0) {
      missing.push(row.question_id);
    }
  }

  return missing;
}

/**
 * telecom_v1 validation: score >= 50 (Developing+) requires
 * at least ONE of: evidence upload, link, or non-empty comment.
 * Applies to ALL questions, not just requires_evidence flagged ones.
 */
export function validateTelecomV1ForSubmission(
  rows: Array<{ question_id: number }>,
  responsesById: Map<
    number,
    { score: number | null; comment: string | null; evidence_links?: string[] }
  >
): number[] {
  const missing: number[] = [];

  for (const row of rows) {
    const resp = responsesById.get(row.question_id);
    if (!resp || resp.score == null) continue;

    // Only enforce for Developing (50) and above
    if (resp.score < 50) continue;

    const hasEvidence = (resp.evidence_links ?? []).length > 0;
    const hasComment = !!(resp.comment && resp.comment.trim().length > 0);

    if (!hasEvidence && !hasComment) {
      missing.push(row.question_id);
    }
  }

  return missing;
}
